/**
 * Phase R — Per-section clone of `pages/WMS/BinToBinTransfer.jsx` (FULL Salem parity).
 *
 * MACHINE-GENERATED by `scripts/build_per_section_wms_setup_frontend.py`.
 * Mounted at `/sections/dispatch/:section/wms-setup/<flow>`. All `${apiBase}/...`
 * API calls are rewritten to `/per-section/${slug}/wms-setup/...`.
 *
 * DO NOT hand-edit. Edit Salem's source of truth and re-run the transformer.
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Package, ArrowRightLeft, Check, Trash2, RefreshCw, ScanLine, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import BarcodeInput from '@/components/BarcodeInput';
import ScanFeedbackOverlay from '@/components/ScanFeedbackOverlay';
import apiClient from '@/services/apiClient';

const SLUG_TO_DISPLAY = {
  color_combo: 'Color Combo',
  art_silk: 'Art Silk',
  kids: 'Kids',
};

// PHASE_R_FULL_CLONE = true (sentinel for tests)

const PerSectionBinToBinTransfer = () => {
  const navigate = useNavigate();
  const { section: slug } = useParams();
  const sectionName = SLUG_TO_DISPLAY[slug];
  const basePath = `/sections/dispatch/${slug}`;
  const apiBase = `/per-section/${slug}/wms-setup`;
  const binInputRef = useRef(null);
  const usnInputRef = useRef(null);

  // State
  const [targetBin, setTargetBin] = useState(null);
  const [targetBinInput, setTargetBinInput] = useState('');
  const [usnInput, setUsnInput] = useState('');
  const [transferredItems, setTransferredItems] = useState([]);
  const [isTransferring, setIsTransferring] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(null);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState('bin'); // 'bin' or 'usn'

  // Computed
  const transferredSet = useMemo(() => new Set(transferredItems.map(i => i.usn_code)), [transferredItems]);

  // Auto-focus management
  useEffect(() => {
    if (step === 'bin' && binInputRef.current) {
      binInputRef.current.focus();
    } else if (step === 'usn' && usnInputRef.current) {
      usnInputRef.current.focus();
    }
  }, [step]);

  const showFeedback = (type, message) => {
    setScanFeedback({ type, message });
    setTimeout(() => setScanFeedback(null), 350);
  };

  const handleBinScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, bin: 'Bin code is required' });
      showFeedback('error', 'Bin Code Required');
      return;
    }

    try {
      const response = await apiClient.get(`${apiBase}/fg/put-away/bin-transfer/validate-bin/${encodeURIComponent(value.trim())}`);
      
      if (!response.data.valid) {
        setErrors({ ...errors, bin: response.data.message });
        showFeedback('error', 'Invalid Bin');
        setTargetBinInput('');
        return;
      }

      setTargetBin(response.data);
      setTargetBinInput('');
      setErrors({ ...errors, bin: null });
      showFeedback('success', `Bin: ${value}`);
      setStep('usn');
      toast.success(`Target bin ${value} validated - ${response.data.current_qty} items currently stored`);
    } catch (error) {
      console.error('Bin validation error:', error);
      setErrors({ ...errors, bin: 'Failed to validate bin' });
      showFeedback('error', 'Validation Error');
      setTargetBinInput('');
    }
  };

  const handleUsnScan = async (value) => {
    if (!value || value.trim() === '') {
      setErrors({ ...errors, usn: 'USN code is required' });
      showFeedback('error', 'USN Required');
      return;
    }

    // Check if already transferred in this session
    if (transferredSet.has(value.trim())) {
      setErrors({ ...errors, usn: 'USN already transferred in this session' });
      showFeedback('error', 'Already Transferred');
      setUsnInput('');
      return;
    }

    try {
      // First validate the USN
      const validateResponse = await apiClient.get(`${apiBase}/fg/put-away/bin-transfer/validate-usn/${encodeURIComponent(value.trim())}`);
      
      if (!validateResponse.data.valid) {
        setErrors({ ...errors, usn: validateResponse.data.message });
        showFeedback('error', 'Invalid USN');
        setUsnInput('');
        return;
      }

      // Check if USN is already in target bin
      if (validateResponse.data.current_bin === targetBin.bin_code) {
        setErrors({ ...errors, usn: `USN is already in bin ${targetBin.bin_code}` });
        showFeedback('error', 'Already in Target');
        setUsnInput('');
        return;
      }

      // Perform the transfer
      setIsTransferring(true);
      const transferResponse = await apiClient.post(`${apiBase}/fg/put-away/bin-transfer/transfer`, {
        usn_code: value.trim(),
        target_bin_code: targetBin.bin_code
      });

      if (transferResponse.data.success) {
        setTransferredItems(prev => [{
          usn_code: transferResponse.data.usn_code,
          item_barcode: transferResponse.data.item_barcode,
          design_name: transferResponse.data.design_name,
          shade: transferResponse.data.shade,
          source_bin: transferResponse.data.source_bin,
          target_bin: transferResponse.data.target_bin,
          timestamp: new Date().toISOString()
        }, ...prev]);

        // Update target bin qty
        setTargetBin(prev => ({
          ...prev,
          current_qty: transferResponse.data.target_bin_qty
        }));

        setUsnInput('');
        setErrors({ ...errors, usn: null });
        showFeedback('success', 'Transferred!');
        toast.success(`USN transferred from ${transferResponse.data.source_bin} to ${targetBin.bin_code}`);

        // Keep focus on USN input for continuous scanning
        setTimeout(() => {
          if (usnInputRef.current) {
            usnInputRef.current.focus();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to transfer USN';
      setErrors({ ...errors, usn: errorMsg });
      showFeedback('error', 'Transfer Failed');
      toast.error(errorMsg);
      setUsnInput('');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleChangeBin = () => {
    setTargetBin(null);
    setStep('bin');
    setTransferredItems([]);
  };

  const handleNewTransfer = () => {
    setTargetBin(null);
    setTargetBinInput('');
    setUsnInput('');
    setTransferredItems([]);
    setStep('bin');
  };
  if (!sectionName) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFFBF5] p-6" data-testid={`persectionbintobintransfer-unknown`}>
        <h2 className="text-lg font-bold">Unknown section</h2>
        <p className="text-sm text-gray-500 mt-1">No catalog entry for slug <code className="font-mono">{slug}</code>.</p>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="bin-to-bin-transfer">
      <ScanFeedbackOverlay feedback={scanFeedback} />
      
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(`${apiBase}/fg`)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display flex items-center gap-2">
                <ArrowRightLeft className="w-6 h-6" />
                Bin to Bin Transfer
              </h1>
              <p className="text-sm text-[#E8DCC8]">Transfer USNs between bins in FG storage</p>
            </div>
          </div>
          {transferredItems.length > 0 && (
            <Button 
              onClick={handleNewTransfer}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              New Transfer
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 space-y-6">
        {/* Step 1: Scan Target Bin */}
        {step === 'bin' && (
          <Card className="border-[#E5DFD0]">
            <CardHeader>
              <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">1</span>
                Scan Target Bin
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Scan or enter the bin code where you want to transfer USNs
              </p>
              <BarcodeInput
                ref={binInputRef}
                value={targetBinInput}
                onChange={setTargetBinInput}
                onScan={handleBinScan}
                placeholder="Scan target bin code (e.g., H01-A-01-L1-B01)"
                error={errors.bin}
                data-testid="bin-input"
              />
            </CardContent>
          </Card>
        )}

        {/* Step 2: Scan USNs (after bin is selected) */}
        {step === 'usn' && targetBin && (
          <>
            {/* Target Bin Info */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Target Bin</p>
                      <p className="text-xl font-bold text-blue-800">{targetBin.bin_code}</p>
                      <p className="text-xs text-blue-600">{targetBin.location_path}</p>
                      {targetBin.existing_design && (
                        <p className="text-xs text-amber-600 mt-1 font-medium">
                          ⚠️ Contains: {targetBin.existing_design.design_name}/{targetBin.existing_design.shade}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-blue-600">Current Items</p>
                    <p className="text-2xl font-bold text-blue-800">{targetBin.current_qty}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleChangeBin}
                      className="text-blue-600 hover:text-blue-700 mt-1"
                    >
                      Change Bin
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* One SKU Per Bin Warning */}
            {targetBin.has_inventory && targetBin.existing_design && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-3">
                  <p className="text-sm text-amber-800">
                    <strong>⚠️ One SKU Per Bin Rule:</strong> This bin contains <strong>{targetBin.existing_design.design_name}/{targetBin.existing_design.shade}</strong>. 
                    Only USNs with the same SKU can be transferred here.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Scan USN Card */}
            <Card className="border-[#E5DFD0]">
              <CardHeader className="pb-3">
                <CardTitle className="text-[#1A1F2E] flex items-center gap-2">
                  <span className="bg-[#2D5F4F] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm">2</span>
                  Scan USNs to Transfer
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Scan USNs to move them to bin <strong>{targetBin.bin_code}</strong>
                </p>
                <BarcodeInput
                  ref={usnInputRef}
                  value={usnInput}
                  onChange={setUsnInput}
                  onScan={handleUsnScan}
                  placeholder="Scan USN code"
                  error={errors.usn}
                  disabled={isTransferring}
                  data-testid="usn-input"
                />
                {isTransferring && (
                  <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Transferring...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transfer Summary */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4 text-center">
                  <Check className="w-6 h-6 mx-auto text-green-600 mb-1" />
                  <p className="text-2xl font-bold text-green-800">{transferredItems.length}</p>
                  <p className="text-xs text-green-600">USNs Transferred</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4 text-center">
                  <Package className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                  <p className="text-2xl font-bold text-blue-800">{targetBin.current_qty}</p>
                  <p className="text-xs text-blue-600">Items in Target Bin</p>
                </CardContent>
              </Card>
            </div>

            {/* Transferred Items Table */}
            {transferredItems.length > 0 && (
              <Card className="border-[#E5DFD0]">
                <CardHeader className="pb-3">
                  <CardTitle className="text-[#1A1F2E]">Transferred Items ({transferredItems.length})</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="rounded-md border overflow-x-auto max-h-[400px] overflow-y-auto">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50">
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>USN Code</TableHead>
                          <TableHead>Design</TableHead>
                          <TableHead>Shade</TableHead>
                          <TableHead>From Bin</TableHead>
                          <TableHead>To Bin</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transferredItems.map((item, idx) => (
                          <TableRow key={item.usn_code} className="bg-green-50/50">
                            <TableCell className="text-gray-500">{transferredItems.length - idx}</TableCell>
                            <TableCell className="font-mono font-medium">{item.usn_code}</TableCell>
                            <TableCell>{item.design_name || '-'}</TableCell>
                            <TableCell>{item.shade || '-'}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-mono text-xs">
                                {item.source_bin}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-green-100 text-green-800 font-mono text-xs">
                                {item.target_bin}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default PerSectionBinToBinTransfer;
